import React, { useMemo } from 'react';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import type { Maybe } from '@soundxyz/utils';
import {
  ERROR_TYPE,
  FEATURES,
  MISCELLANEOUS_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { Button } from '@components/main-components/button/Button';
import { setFollowingStatus } from '@components/modals/UserStats/store';
import { useAuth } from '@context/auth/base';
import { useToast } from '@context/toast';
import { RefetchOperationsOnComplete } from '@graphql/helpers/effects';
import {
  FollowUserDocument,
  GetArtistIdByUserIdDocument,
  gql,
  IsSubscribedToArtistsNotificationsDocument,
  SubscribeToArtistDocument,
  UnsubscribeToArtistDocument,
  useMutation,
  useQuery,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { createBatchingInputStore } from '@store/batchInput';

/*#__PURE__*/ gql(/* GraphQL */ `
  query IsSubscribedToArtistsNotifications(
    $pagination: ArtistNotificationSubscriptionCursorConnectionArgs!
    $artists: [UUID!]!
  ) {
    artistNotificationSubscriptions(pagination: $pagination, artists: $artists) {
      edges {
        node {
          id
          artistId
        }
      }
    }
  }
`);

RefetchOperationsOnComplete({
  trigger: [SubscribeToArtistDocument, UnsubscribeToArtistDocument],
  refetch: [IsSubscribedToArtistsNotificationsDocument],
});

const { useBatchedKey } = createBatchingInputStore({
  chunkLimit: 50,
});

export function useIsSubscribedToArtist({ artistId }: { artistId: Maybe<string> }) {
  const artistIds = useBatchedKey({
    key: artistId,
  });

  const { isAuthenticatedWithAPI } = useAuth();

  const { data: subscribeData, isLoading: isSubscribedLoading } = useQuery(
    IsSubscribedToArtistsNotificationsDocument,
    {
      variables: !!artistIds?.length && {
        pagination: {
          first: artistIds.length,
        },
        artists: artistIds,
      },
      keepPreviousData: isAuthenticatedWithAPI,
      enabled: isAuthenticatedWithAPI,
      staleTime: 0,
    },
  );

  const isSubscribedToArtist = useMemo(() => {
    return (
      subscribeData?.data.artistNotificationSubscriptions.edges.some(
        value => value.node.artistId === artistId,
      ) ?? false
    );
  }, [subscribeData, artistId]);

  return {
    isSubscribedToArtist,
    isSubscribedLoading,
  };
}

export const SubscribeButton = ({ artistId, userId }: { artistId: string; userId: string }) => {
  const { openToast } = useToast();

  const { isSubscribedToArtist, isSubscribedLoading } = useIsSubscribedToArtist({
    artistId,
  });

  const { data: artistData } = useQuery(GetArtistIdByUserIdDocument, {
    variables: {
      userId,
    },
    staleTime: 0,
  });

  const artistName = useMemo(() => artistData?.data.user?.artist?.name ?? 'artist', [artistData]);

  const { mutateAsync: followUserMutation } = useMutation(FollowUserDocument, {});
  const { mutateAsync: subscribeToArtist } = useMutation(SubscribeToArtistDocument, {});
  const { mutateAsync: unsubscribeToArtist } = useMutation(UnsubscribeToArtistDocument, {});

  const logError = useLogError();

  const onClick = useStableCallback(() => {
    if (isSubscribedToArtist) {
      unsubscribeToArtist({
        input: {
          artists: [artistId],
        },
      })
        .then(() => {
          openToast({
            text: `Successfully unsubscribed ${artistName}`,
            variant: 'success',
          });
        })
        .catch(error => {
          logError({
            action: MISCELLANEOUS_ERROR_ACTIONS.SUBSCRIBE_UNSUBSCRIBE_ERROR,
            error,
            errorType: ERROR_TYPE.UNKNOWN,
            isArtistOnlyPage: false,
            level: 'error',
            message: 'Error calling unsubscribeToArtistMutation',
            feature: FEATURES.FOLLOW_SUBSCRIBE,
            unindexedExtra: {
              artistIdToSubscribeUnsubscribe: artistId,
            },
            toast: `Error unsubscribing ${artistName}.`,
          });
        });
    } else {
      Promise.all([
        followUserMutation({ input: { user: userId } }),
        subscribeToArtist({
          input: {
            artist: artistId,
          },
        }),
      ])
        .then(() => {
          openToast({
            text: `Successfully subscribed ${artistName}`,
            variant: 'success',
          });
        })
        .catch(error => {
          setFollowingStatus({
            userId,
            isFollowing: false,
          });

          logError({
            action: MISCELLANEOUS_ERROR_ACTIONS.SUBSCRIBE_UNSUBSCRIBE_ERROR,
            error,
            errorType: ERROR_TYPE.UNKNOWN,
            isArtistOnlyPage: false,
            level: 'error',
            message: 'Error calling subscribeToArtistMutation',
            feature: FEATURES.FOLLOW_SUBSCRIBE,
            unindexedExtra: {
              artistIdToSubscribeUnsubscribe: artistId,
            },
            toast: `Error subscribing ${artistName}.`,
          });
        });
    }
  });

  const variant = isSubscribedToArtist ? 'secondary' : 'primary';
  const label = isSubscribedToArtist ? 'Subscribed' : 'Get Notified';

  return (
    <Button
      className="min-w-[81px]"
      label={label}
      variant={variant}
      onClick={onClick}
      size="XS"
      loading={isSubscribedLoading}
    />
  );
};
